/**
 * Legacy Encrypted Value Migration (ECHO-2318 / VAPT-006)
 *
 * Tokens and the session id used to be AES-encrypted with a key shipped in the JS bundle,
 * which is obfuscation rather than protection. They are now stored as plain values in
 * sessionStorage. Sessions that are live when the change is deployed still hold the old
 * ciphertext, so reads decrypt it once and the caller writes the plain value back.
 */

/**
 * `CryptoJS.AES.encrypt(...).toString()` output: standard base64 of whole AES blocks.
 * JWT/JWE tokens contain dots and UUIDs contain hyphens, so plain values never match.
 */
const LEGACY_CIPHERTEXT_PATTERN = /^[A-Za-z0-9+/]+={0,2}$/;
const BASE64_QUANTUM = 4;

export interface ResolvedStoredValue {
  /** The plain value, or null when nothing usable is stored. */
  readonly value: string | null;
  /** True when `value` was recovered from legacy ciphertext and should be written back. */
  readonly migrated: boolean;
}

export function isLegacyCiphertext(raw: string): boolean {
  return raw.length % BASE64_QUANTUM === 0 && LEGACY_CIPHERTEXT_PATTERN.test(raw);
}

/**
 * Resolve a raw sessionStorage value to its plain form.
 * - Plain values (anything that is not legacy ciphertext) are returned as-is.
 * - Legacy ciphertext is decrypted and flagged for write-back.
 * - Legacy ciphertext that cannot be decrypted resolves to null, so it is never sent as a
 *   credential; the caller should remove the stored key.
 */
export function resolveStoredValue(
  raw: string | null,
  decrypt: (ciphertext: string) => string,
): ResolvedStoredValue {
  if (!raw) return { value: null, migrated: false };
  if (!isLegacyCiphertext(raw)) return { value: raw, migrated: false };

  let decrypted = "";
  try {
    decrypted = decrypt(raw);
  } catch {
    // Not decryptable with the legacy key: fall through and discard the value.
  }

  return decrypted ? { value: decrypted, migrated: true } : { value: null, migrated: false };
}
